import { createContext, useContext } from 'react'
import type { PullQuoteBlock, ThoughtBlock, UnfoldBlock } from '../../lib/types'
import type { NoteControls, UnfoldControls } from './InlineText'

/**
 * What a reader can do with the page's words (the public page only; the
 * author's preview passes none). Everything here already merges the server's
 * payload, optimistic takes and — signed out — the local queue.
 */
export interface TakeActions {
  /** Lines the viewer took, by block id (normalised excerpts). */
  lines: ReadonlyMap<string, readonly string[]>
  /** Blocks whose whole quote the viewer took. */
  quotes: ReadonlySet<string>
  /** Distinct readers who took anything from each block. */
  counts: Readonly<Record<string, number>>
  /** A quote take in flight. */
  busyId: string | null
  takeLine(blockId: string, excerpt: string): void
  /** Selected words, one part per block: a line, or a passage over several paragraphs (a line per block). */
  takePassage(parts: ReadonlyArray<{ blockId: string; excerpt: string }>): void
  takeQuote(blockId: string): void
  copyLine(excerpt: string): void
  copyQuote(block: ThoughtBlock | PullQuoteBlock): void
  shareQuote(block: ThoughtBlock | PullQuoteBlock): void
}

/** Page-wide reader state the block renderers share (stable between scroll frames). */
export interface ReaderContextValue {
  takes?: TakeActions
  showTimes: boolean
  authorName: string
  unfolds:ReadonlyMap<string, UnfoldBlock>
  openUnfolds: ReadonlySet<string>
  /** The same, shaped for the inline renderer (stable until an unfold opens or closes). */
  unfoldControls: UnfoldControls
  closeUnfold(anchor: string): void
  /** Words a margin note is about: they show their note (stable while the blocks are). */
  notes: NoteControls
  /** The margin note just shown from its words, lit for a moment. */
  litNote: string | null
  /** The first voice memo starts open. */
  firstVoiceId: string | null
  /** Quote whose action bar is pinned open (touch: tap the quote). */
  activeQuote: string | null
  setActiveQuote(id: string | null): void
  /** Narration has started (playing, or paused part-way): paragraphs offer "Read from here". */
  listening: boolean
  /** Blocks the narration reads. */
  narrated: ReadonlySet<string>
  seekTo(blockId: string): void
}

export const ReaderContext = createContext<ReaderContextValue | null>(null)

export function useReader(): ReaderContextValue {
  const ctx = useContext(ReaderContext)
  if (!ctx) throw new Error('useReader must be used inside <ArticleView>')
  return ctx
}

/** id of a margin note's element, for aria-controls and scrolling to it. */
export const marginNoteId = (blockId: string) => `note-${blockId}`

/** id of an unfold's card, for aria-controls. */
export const unfoldCardId = (anchor: string) => `unfold-${anchor}`
